import { useOverlayState } from "@heroui/react";
import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "@/src/common/components/ui";
import { toastService } from "@/src/common/services/toast.service";

interface McpModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const MCP_SERVER_URL = "https://mcp.upsquad.us/mcp";

const CLAUDE_CONFIG = {
	mcpServers: {
		upsquad: {
			command: "npx",
			args: ["mcp-remote@latest", MCP_SERVER_URL],
		},
	},
};

const CHATGPT_CONFIG = {
	mcpServers: {
		upsquad: {
			url: MCP_SERVER_URL,
		},
	},
};

const GEMINI_CONFIG = {
	mcpServers: {
		upsquad: {
			command: "npx",
			args: ["mcp-remote@latest", MCP_SERVER_URL],
		},
	},
};

export function McpModal({ isOpen, onClose }: McpModalProps) {
	const { t } = useTranslation("shell");
	const state = useOverlayState({ isOpen, onOpenChange: (open) => !open && onClose() });

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		toastService.success(t("mcp.copied"));
	};

	return (
		<Modal state={state}>
			<Modal.Backdrop>
				<Modal.Container size="lg">
					<Modal.Dialog>
						<Modal.Header>
							<h2 className="text-lg font-semibold">{t("mcp.title")}</h2>
						</Modal.Header>
						<Modal.Body className="flex flex-col gap-6">
							<p className="text-sm text-foreground/70">{t("mcp.description")}</p>

							<div className="flex items-center justify-between rounded-lg border border-divider bg-content2 px-4 py-3">
								<div className="flex flex-col gap-1">
									<span className="text-xs font-medium text-foreground/50">{t("mcp.server_url")}</span>
									<code className="text-sm">{MCP_SERVER_URL}</code>
								</div>
								<Button variant="ghost" size="sm" onPress={() => copyToClipboard(MCP_SERVER_URL)}>
									<Copy size={14} />
								</Button>
							</div>

							<PlatformSection
								title={t("mcp.claude_title")}
								instructions={t("mcp.claude_instructions")}
								config={CLAUDE_CONFIG}
								onCopy={() => copyToClipboard(JSON.stringify(CLAUDE_CONFIG, null, 2))}
								copyLabel={t("mcp.copy_config")}
							/>

							<PlatformSection
								title={t("mcp.chatgpt_title")}
								instructions={t("mcp.chatgpt_instructions")}
								config={CHATGPT_CONFIG}
								onCopy={() => copyToClipboard(JSON.stringify(CHATGPT_CONFIG, null, 2))}
								copyLabel={t("mcp.copy_config")}
							/>

							<PlatformSection
								title={t("mcp.gemini_title")}
								instructions={t("mcp.gemini_instructions")}
								config={GEMINI_CONFIG}
								onCopy={() => copyToClipboard(JSON.stringify(GEMINI_CONFIG, null, 2))}
								copyLabel={t("mcp.copy_config")}
								note={t("mcp.gemini_note")}
							/>
						</Modal.Body>
						<Modal.Footer className="flex justify-end">
							<Button variant="ghost" onPress={onClose}>
								{t("mcp.close")}
							</Button>
						</Modal.Footer>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}

interface PlatformSectionProps {
	title: string;
	instructions: string;
	config: object;
	onCopy: () => void;
	copyLabel: string;
	note?: string;
}

function PlatformSection({ title, instructions, config, onCopy, copyLabel, note }: PlatformSectionProps) {
	return (
		<div className="flex flex-col gap-2">
			<h3 className="text-sm font-semibold">{title}</h3>
			<p className="text-xs text-foreground/70">{instructions}</p>
			<pre className="overflow-x-auto rounded-lg bg-content2 p-3 text-xs">
				<code>{JSON.stringify(config, null, 2)}</code>
			</pre>
			{note ? <p className="text-xs italic text-warning">{note}</p> : null}
			<Button variant="ghost" size="sm" className="self-end" onPress={onCopy}>
				<Copy size={14} />
				{copyLabel}
			</Button>
		</div>
	);
}
